import localStorageAccessor from "./local-storage-accessor";
import userService from "./user-service";
export var Permission;
(function (Permission) {
    // Dashboard
    Permission["DASHBOARD_VIEW"] = "Dashboard.View";
    // Nodes
    Permission["NODES_VIEW"] = "Nodes.View";
    Permission["NODES_CREATE"] = "Nodes.Create";
    Permission["NODES_EDIT"] = "Nodes.Edit";
    /** Measurements, communication, temperature hardware fields on Edit Node (with Super Admin). */
    Permission["NODES_HARDWARE_SETTINGS"] = "Nodes.HardwareSettings";
    Permission["NODES_DELETE"] = "Nodes.Delete";
    // Alarms
    Permission["ALARMS_VIEW"] = "Alarms.View";
    Permission["ALARMS_ACKNOWLEDGE"] = "Alarms.Acknowledge";
    Permission["ALARMS_CLEAR"] = "Alarms.Clear";
    // Insights
    Permission["INSIGHTS_VIEW"] = "Insights.View";
    // Reports
    Permission["REPORTS_VIEW"] = "Reports.View";
    Permission["REPORTS_GENERATE"] = "Reports.Generate";
    Permission["REPORTS_EXPORT"] = "Reports.Export";
    // Settings
    Permission["SETTINGS_VIEW"] = "Settings.View";
    Permission["SETTINGS_EDIT"] = "Settings.Edit";
    // Users
    Permission["USERS_VIEW"] = "Users.View";
    Permission["USERS_CREATE"] = "Users.Create";
    Permission["USERS_EDIT"] = "Users.Edit";
    Permission["USERS_DELETE"] = "Users.Delete";
    // Access Points
    Permission["ACCESSPOINTS_VIEW"] = "AccessPoints.View";
    Permission["ACCESSPOINTS_CREATE"] = "AccessPoints.Create";
    Permission["ACCESSPOINTS_EDIT"] = "AccessPoints.Edit";
    /** AP Edit → Server Settings tab (with Super Admin). */
    Permission["ACCESSPOINTS_SERVER_SETTINGS"] = "AccessPoints.ServerSettings";
    Permission["ACCESSPOINTS_DELETE"] = "AccessPoints.Delete";
    // System
    Permission["SYSTEM_SETTINGS"] = "System.Settings";
    Permission["SYSTEM_LOGS"] = "System.Logs";
    Permission["ROLES_VIEW"] = "Roles.View";
    Permission["ROLES_CREATE"] = "Roles.Create";
    Permission["ROLES_EDIT"] = "Roles.Edit";
    Permission["ROLES_DELETE"] = "Roles.Delete";
})(Permission || (Permission = {}));
export class PermissionChecker {
    /**
     * Get user permissions from localStorage
     * Returns normalized array of permission strings
     */
    static getUserPermissions() {
        const user = localStorageAccessor.getUser();
        if (!user || !user.permissions) {
            return [];
        }
        // Normalize permissions: trim whitespace and filter empty strings
        return user.permissions
            .map((p) => String(p).trim())
            .filter((p) => p.length > 0);
    }
    /**
     * Normalize a permission value to string for comparison
     */
    static normalizePermission(permission) {
        return String(permission).trim();
    }
    /**
     * Check if user has a specific permission
     */
    static hasPermission(permission) {
        const permissions = this.getUserPermissions();
        if (permissions.length === 0) {
            return false;
        }
        const normalizedPermission = this.normalizePermission(permission);
        return permissions.includes(normalizedPermission);
    }
    /**
     * Check if user has ANY of the required permissions
     */
    static hasAnyPermission(requiredPermissions) {
        if (!requiredPermissions || requiredPermissions.length === 0) {
            return true; // No requirements means access granted
        }
        const userPermissions = this.getUserPermissions();
        if (userPermissions.length === 0) {
            return false; // No user permissions means no access
        }
        // Normalize and check if any required permission matches
        const normalizedRequired = requiredPermissions.map((p) => this.normalizePermission(p));
        return normalizedRequired.some((reqPerm) => userPermissions.includes(reqPerm));
    }
    /**
     * Check if user has ALL of the required permissions
     */
    static hasAllPermissions(requiredPermissions) {
        if (!requiredPermissions || requiredPermissions.length === 0) {
            return true; // No requirements means access granted
        }
        const userPermissions = this.getUserPermissions();
        if (userPermissions.length === 0) {
            return false; // No user permissions means no access
        }
        // Normalize and check if all required permissions are present
        const normalizedRequired = requiredPermissions.map((p) => this.normalizePermission(p));
        return normalizedRequired.every((reqPerm) => userPermissions.includes(reqPerm));
    }
    /**
     * Check if user can access a route based on required permissions
     */
    static canAccessRoute(requiredPermissions) {
        if (!requiredPermissions || requiredPermissions.length === 0) {
            return true; // No requirements means access granted
        }
        return this.hasAnyPermission(requiredPermissions);
    }
    /**
     * Get all user permissions (for debugging)
     */
    static getAllPermissions() {
        return this.getUserPermissions();
    }
}
/** Edit AP → Server Settings tab: Super Admin or explicit role permission. */
export function canAccessApServerSettingsTab() {
    return (userService.hasRole("Super Admin") ||
        PermissionChecker.hasPermission(Permission.ACCESSPOINTS_SERVER_SETTINGS));
}
/**
 * Free roaming is a Super Admin-only setting: the role alone grants it, no permission
 * substitutes for it (unlike the hardware blocks below). The server enforces the same rule and
 * returns 403 on any unauthorised change, so this only decides what the UI offers.
 */
export function canEditFreeRoamingNode() {
    return userService.hasRole("Super Admin");
}
/** Edit Node → WPT measurement/communication + temperature hardware blocks. */
export function canAccessNodeHardwareSections() {
    return (userService.hasRole("Super Admin") ||
        PermissionChecker.hasPermission(Permission.NODES_HARDWARE_SETTINGS));
}
